import { useContext, useEffect } from "react";
import { Box, Container, Grid, Typography } from "@material-ui/core";
import styled from "@emotion/styled";

import HeroIllustration from "@/public/images/HeroIllustration.svg";
import MainLayout from "@/layouts/MainLayout";
import DividerWithText from "@/components/DividerWithText";
import StepsLanding from "@/components/StepsLanding";
import { CTAButton, SupportButton } from "@/components/Buttons";
import { useIsMobile, useIsMedium } from "@/styles/breakpoints";
import intercom from "@/services/intercom";
import { IsBusinessContext } from "../contexts";
import Links from "@/constants/Links";

const partnerLogos = ["atbay", "axis", "cfc", "corvus", "cowbell", "hiscox"];

const List = styled.ul`
  padding: 0;
  list-style: none;
  margin-bottom: 32px;
`;

const ListItem = styled.li`
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: #000;
  line-height: 2;

  &:before {
    content: "";
    display: inline-block;
    height: 24px;
    width: 24px;
    position: relative;
    top: 3px;
    background-position: center center;
    margin-right: 20px;
    background-size: cover;
    background-image: url("data:image/svg+xml,%3Csvg width='45' height='45' viewBox='0 0 45 45' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0)'%3E%3Cpath d='M41.9831 11.2059C39.9711 7.75881 37.2418 5.02956 33.7947 3.0176C30.3469 1.00573 26.5832 0 22.5007 0C18.4185 0 14.6537 1.00573 11.2066 3.0176C7.75922 5.02926 5.02997 7.7585 3.0179 11.2059C1.00573 14.6534 0 18.4182 0 22.5001C0 26.5821 1.00604 30.3463 3.0176 33.794C5.02956 37.2411 7.75881 39.9703 11.2062 41.9825C14.6537 43.9943 18.4182 45.0001 22.5003 45.0001C26.5823 45.0001 30.3472 43.9943 33.7946 41.9825C37.2417 39.9711 39.9708 37.2411 41.9827 33.794C43.9943 30.3466 44.9999 26.5819 44.9999 22.5001C45 18.4178 43.9943 14.6529 41.9831 11.2059ZM37.0896 19.0723L21.1818 34.9803C20.8106 35.3517 20.3617 35.5373 19.834 35.5373C19.3263 35.5373 18.887 35.3517 18.5157 34.9803L7.90986 24.375C7.55871 24.0232 7.38273 23.5846 7.38273 23.0567C7.38273 22.51 7.5581 22.0604 7.90986 21.7088L10.5759 19.0723C10.9475 18.7013 11.3863 18.5157 11.8945 18.5157C12.4026 18.5157 12.8421 18.7014 13.2131 19.0723L19.8343 25.6933L31.7874 13.7696C32.1586 13.3984 32.5979 13.2128 33.1057 13.2128C33.6131 13.2128 34.0532 13.3984 34.424 13.7696L37.09 16.4065C37.4422 16.7578 37.6175 17.207 37.6175 17.7537C37.6175 18.2812 37.4422 18.7207 37.0896 19.0723Z' fill='%232B33FF'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0'%3E%3Crect width='45' height='45' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
  }
`;

const HeroIllustrationContainer = styled(Box)`
  position: absolute;
  right: 0;
  top: 64px;
  width: 640px;
  z-index: -1;
`;

const PartnerLogo = styled.img`
  max-height: 120px;
  max-width: 150px;
  object-fit: contain;
`;

const CTAHeader = styled(Box)`
  color: #2b33ff;
  font-size: 85px;
  font-weight: 100;
  margin-bottom: 48px;
`;

const CTAText = styled(Typography)`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  margin-top: 24px;
`;

const StepSectionContainer = styled(Box)`
  background: linear-gradient(
    114.21deg,
    rgba(202, 206, 255, 0.5) 0.38%,
    rgba(255, 242, 180, 0.5) 98.76%
  );
  padding: 128px 0;
  clip-path: polygon(0 0, 100% 5%, 100% 100%, 0 95%);

  @media (min-width: 1420px) {
    clip-path: polygon(0 0, 100% 10%, 100% 100%, 0 90%);
  }
`;

const BackgroundImageContainer = styled.div`
  position: absolute;
  right: 0;
  width: 100%;
`;

const StepSectionBackgroundImage = styled.img`
  float: right;
  width: 65%;
  max-width: 1420px;
  z-index: 1;
  display: none;
  margin-top: -96px;

  @media (min-width: 768px) {
    display: block;
  }

  @media (min-width: 1420px) {
    margin-top: -128px;
  }
`;

const TimerImage = styled.img`
  position: absolute;
  right: 15%;
  width: 96px;

  @media (min-width: 1420px) {
    margin-top: 64px;
  }
`;

const CTASectionBackgroundImage = styled.img`
  position: absolute;
  right: 0;
  height: 800px;
  margin-top: -800px;
  z-index: -1;
`;

export default function Home() {
  const isMobile = useIsMobile();
  const isMedium = useIsMedium();
  const { setIsBusiness } = useContext(IsBusinessContext);

  useEffect(() => {
    setIsBusiness(true);
  });

  return (
    <MainLayout
      backgroundImage={
        <CTASectionBackgroundImage
          src="/images/background_waves_2.png"
          alt="Background Waves"
        />
      }
    >
      <div>
        <Container>
          <Box mt={isMobile ? 3 : 7} mb={12}>
            <Grid container>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h1"
                  style={isMobile ? { fontSize: 40 } : {}}
                >
                  <span style={{ fontWeight: 300 }}>
                    Get a Cyber Insurance Quote
                  </span>
                  <br />
                  <span style={{ fontWeight: 500 }}>In 60 Seconds</span>
                </Typography>

                <Box>
                  <List>
                    <ListItem>Instant Quotes from 6 Top Insurers</ListItem>
                    <ListItem>Up to $3M in Insurance</ListItem>
                    <ListItem>Comprehensive Coverage</ListItem>
                    <ListItem>24/7 Expert Support</ListItem>
                    <ListItem>No Phone Call Required</ListItem>
                  </List>

                  <Box width={300}>
                    <CTAButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      route={Links.QUOTE}
                    >
                      Get a quote
                    </CTAButton>
                  </Box>
                </Box>
              </Grid>
              {isMedium && (
                <Grid item xs={4}>
                  <Box position="relative">
                    <HeroIllustrationContainer>
                      <img
                        src={HeroIllustration}
                        alt="Woman standing next to a shield"
                        style={{ width: "100%" }}
                      />
                    </HeroIllustrationContainer>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box mb={8}>
            <DividerWithText pb={4}>Quote With</DividerWithText>
            <Grid container alignItems="center" justifyContent="center">
              {partnerLogos.map((partnerLogo) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={partnerLogo}>
                  <Box display="flex" justifyContent="center" py={2}>
                    <PartnerLogo
                      src={`/images/${partnerLogo}.png`}
                      alt={partnerLogo}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>

        <StepSectionContainer py={4}>
          <BackgroundImageContainer>
            <StepSectionBackgroundImage
              src="/images/background_waves_1.png"
              alt="Background Waves"
            />
            {!isMobile && <TimerImage src="/images/timer.png" alt="Timer" />}
          </BackgroundImageContainer>

          <Container>
            <Grid container>
              <Grid item xs={12} sm={10} md={8} lg={6}>
                <Box mb={11}>
                  <Box pb={3}>
                    <Typography
                      variant="h1"
                      style={isMobile ? { fontSize: 36 } : {}}
                    >
                      Quote and Buy
                    </Typography>
                    <Typography
                      variant="h2"
                      style={isMobile ? { fontSize: 36 } : {}}
                    >
                      on the Fastest Cyber Insurance Marketplace
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2">
                    As your digital broker, we take care of all the paperwork.{" "}
                    <b>It’s easy as 1-2-3.</b>
                  </Typography>
                </Box>
              </Grid>
              <Grid item />
            </Grid>

            <StepsLanding
              stepTitles={["Apply", "Quote", "Buy"]}
              bullets={[
                [
                  {
                    title: "No time wasted",
                    description:
                      "There’s no sign-up process or phone call necessary. Just fill out one short application.",
                  },
                  {
                    title: "Help ready",
                    description:
                      "Our expert broker team is available 24/7 to chat if you have any questions.",
                  },
                ],
                [
                  {
                    title: "Instant Quotes",
                    description: "It won’t take more than a few minutes.",
                  },
                  {
                    title: "Education",
                    description:
                      "Every quote comes with helpful cybersecurity resources and reports.",
                  },
                  {
                    title: "Analysis",
                    description:
                      "We provide a detailed comparison of all quotes. Our AI recommends the best one.",
                  },
                ],
                [
                  {
                    title: "Buy Online",
                    description: "One click is all it takes.",
                  },
                  {
                    title: "Sleep Easy",
                    description: "Know that you're covered.",
                  },
                ],
              ]}
            />
          </Container>
        </StepSectionContainer>

        <Container>
          <Box>
            <Box px={10} mb={11}>
              <Grid container spacing={10} alignItems="center">
                <Grid item xs={12} md={6}>
                  <CTAHeader
                    style={isMobile ? { fontSize: 64, fontWeight: 200 } : {}}
                  >
                    Get Covered
                  </CTAHeader>
                  <CTAText>
                    Protect yourself from cyber risk and get insurance today.
                  </CTAText>
                  <CTAText>
                    We’ve made it so easy that you won’t need any help. But we
                    provide 24/7 support anyway.
                  </CTAText>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    margin="auto"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box mb={7}>
                      <CTAButton
                        color="primary"
                        variant="contained"
                        route={Links.QUOTE}
                      >
                        Get a Quote
                      </CTAButton>
                    </Box>
                    <SupportButton
                      color="secondary"
                      variant="outlined"
                      onClick={intercom.show}
                    >
                      Talk to Support
                    </SupportButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </div>
    </MainLayout>
  );
}
